import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';

const Login = () => {
  const navigate = useNavigate();
  const { login, authLoading, user, isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [resetMessage, setResetMessage] = useState('');
  const [resetSending, setResetSending] = useState(false);

  // Redirect admins to admin dashboard after login
  useEffect(() => {
    if (user && isAdmin) {
      navigate('/admin/dashboard');
    } else if (user && !isAdmin) {
      navigate('/dashboard');
    }
  }, [user, isAdmin, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        // Navigation will be handled by useEffect when user/isAdmin state updates
        // Don't navigate here - wait for user profile to load to determine admin status
      } else {
        setErrors({ general: result.error || 'Invalid email or password' });
      }
    } catch (error) {
      setErrors({ general: 'Login failed. Please try again.' });
    }
  };

  const handleForgotPassword = async () => {
    setResetMessage('');
    setErrors(prev => ({ ...prev, general: '' }));
    if (!formData.email.trim()) {
      setErrors(prev => ({ ...prev, email: 'Email is required to reset' }));
      return;
    }
    setResetSending(true);
    try {
      const redirectBase = process.env.REACT_APP_SITE_URL || 'http://localhost:3000';
      const redirectTo = `${redirectBase}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email.trim(), { redirectTo });
      if (error) {
        setResetMessage('Could not send reset link. Please try again.');
      } else {
        setResetMessage('If an account exists, a reset link has been sent.');
      }
    } catch (err) {
      setResetMessage('Could not send reset link. Please try again.');
    } finally {
      setResetSending(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="mb-8">
        <Link to="/" className="flex items-center text-gray-600 hover:text-primary-600 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
        <p className="text-gray-600">Sign in to your account to continue</p>
      </div>

      <div className="card">
        {errors.general && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`input-field pl-10 ${errors.email ? 'border-red-500' : ''}`}
                placeholder="Enter your email"
                disabled={authLoading}
              />
            </div>
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`input-field pl-10 ${errors.password ? 'border-red-500' : ''}`}
                placeholder="Enter your password"
                disabled={authLoading}
              />
            </div>
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
          </div>

          <button
            type="submit"
            disabled={authLoading}
            className="w-full btn-primary py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {authLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-4 text-right">
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={resetSending}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium disabled:opacity-60"
          >
            {resetSending ? 'Sending reset link...' : 'Forgot password?'}
          </button>
        </div>

        {resetMessage && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
            {resetMessage}
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login; 